import { Injectable } from '@angular/core';
import { drawDOM, DrawOptions, exportImage, exportPDF, geometry, Group, Text } from '@progress/kendo-drawing';
import { saveAs } from '@progress/kendo-file-saver';
import { Workbook, WorkbookSheetColumn, WorkbookSheetRow } from '@progress/kendo-angular-excel-export';
import pptxgen from 'pptxgenjs';
import { ChartNode } from '../models/chart-node.model';
import { Size } from './org-chart-pagination';

// PDF viewers struggle with pages over 200in (14400pt); browsers cap canvases at ~16k px a side.
const MAX_PDF_POINTS = 14000;
const MAX_IMAGE_PIXELS = 16000;
const PX_TO_PT = 0.75;

const TYPE_LABELS: Record<ChartNode['type'], string> = {
  organization: 'Organization',
  position: 'Position',
  employee: 'Employee',
};

export type PaperSize = 'A4' | 'A3';

// Landscape page sizes in points
const PAPER_SIZES: Record<PaperSize, Size> = {
  A4: { width: 842, height: 595 },
  A3: { width: 1191, height: 842 },
};
const PAGE_MARGIN = 28;
const PAGE_HEADER = 44;
const PAGE_FOOTER = 18;

// Widescreen slide, in inches
const SLIDE = { width: 13.333, height: 7.5 };
const SLIDE_IMAGE = { x: 0.4, y: 1.0, width: SLIDE.width - 0.8, height: SLIDE.height - 1.5 };
const POINTS_PER_INCH = 72;

// One page of a paged export. `render` puts the page's content on screen and returns the element to draw.
export interface ExportPage {
  subtitle: string;
  render: () => Promise<HTMLElement>;
}

export interface CapturedImage {
  dataUrl: string;
  width: number;
  height: number;
}

@Injectable({
  providedIn: 'root'
})
export class OrgChartExportService {

  // PDF / PNG

  async exportPdf(element: HTMLElement, fileName: string) {
    const scale = Math.min(1, MAX_PDF_POINTS / (Math.max(element.scrollWidth, element.scrollHeight) * PX_TO_PT));
    const group = await this.draw(element, { paperSize: 'auto', margin: { top: '1cm', right: '1cm', bottom: '1cm', left: '1cm' }, scale });
    const dataUrl = await exportPDF(group, { title: fileName });
    saveAs(dataUrl, `${fileName}.pdf`);
  }

  async exportPng(element: HTMLElement, fileName: string) {
    const image = await this.captureImage(element);
    saveAs(image.dataUrl, `${fileName}.png`);
  }

  async captureImage(element: HTMLElement): Promise<CapturedImage> {
    const group = await this.draw(element);
    const size = group.bbox().size;
    const scale = Math.min(1, MAX_IMAGE_PIXELS / Math.max(size.width, size.height));
    const width = Math.round(size.width * scale);
    const height = Math.round(size.height * scale);
    const dataUrl = await exportImage(group, { width, height, cors: 'anonymous' });
    return { dataUrl, width, height };
  }

  private async draw(element: HTMLElement, options: DrawOptions = {}): Promise<Group> {
    const restoreImages = await this.inlineImages(element);
    try {
      return await drawDOM(element, options);
    } finally {
      restoreImages();
    }
  }

  // Cross-origin photos would be skipped or taint the canvas, so swap them for data URLs while
  // drawing. Photos that can't be fetched are hidden. Returns a function that undoes the changes.
  private async inlineImages(element: HTMLElement): Promise<() => void> {
    const changes: (() => void)[] = [];
    const cache = new Map<string, Promise<string | null>>();
    const images = Array.from(element.querySelectorAll('img'));

    await Promise.all(images.map(async img => {
      const src = img.currentSrc || img.src;
      if (!src || src.startsWith('data:') || new URL(src, location.href).origin === location.origin) {
        return;
      }
      if (!cache.has(src)) {
        cache.set(src, this.toDataUrl(src));
      }
      const dataUrl = await cache.get(src);
      if (dataUrl) {
        img.src = dataUrl;
        changes.push(() => { img.src = src; });
      } else {
        const visibility = img.style.visibility;
        img.style.visibility = 'hidden';
        changes.push(() => { img.style.visibility = visibility; });
      }
    }));

    return () => changes.forEach(undo => undo());
  }

  private async toDataUrl(src: string): Promise<string | null> {
    try {
      const response = await fetch(src, { mode: 'cors' });
      if (!response.ok) {
        return null;
      }
      const blob = await response.blob();
      return await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch {
      return null;
    }
  }

  // Excel

  async exportExcel(roots: ChartNode[], fileName: string, includeEmployees = true) {
    const workbook = new Workbook({ sheets: [this.buildSheet(roots, fileName, includeEmployees)] });
    const dataUrl = await workbook.toDataURL();
    saveAs(dataUrl, `${fileName}.xlsx`);
  }

  buildSheet(roots: ChartNode[], title: string, includeEmployees = true) {
    const rows = this.flatten(roots, includeEmployees);
    // Every data field that appears anywhere in the chart, in first-seen order
    const dataKeys: string[] = [];
    rows.forEach(row => Object.keys(row.node.data).forEach(key => {
      if (!dataKeys.includes(key)) {
        dataKeys.push(key);
      }
    }));

    const headers = ['Level', 'Type', 'Name', 'Reports To', 'Path', ...dataKeys];
    const header: WorkbookSheetRow = {
      cells: headers.map(value => ({ value, bold: true, background: '#eeeeee' })),
    };
    const body: WorkbookSheetRow[] = rows.map(row => ({
      cells: [
        { value: row.level },
        { value: TYPE_LABELS[row.node.type] },
        { value: row.node.name, indent: row.level - 1 },
        { value: row.parent?.name ?? '' },
        { value: row.path },
        ...dataKeys.map(key => ({ value: row.node.data[key] ?? '' })),
      ],
    }) as WorkbookSheetRow);

    const columns: WorkbookSheetColumn[] = headers.map(() => ({ autoWidth: true }));
    return {
      name: title.substring(0, 31),
      columns,
      frozenRows: 1,
      filter: { from: 0, to: headers.length - 1 },
      rows: [header, ...body],
    };
  }

  // Depth-first rows; employees follow the node they belong to, one level deeper.
  flatten(roots: ChartNode[], includeEmployees = true): { node: ChartNode, parent: ChartNode | null, level: number, path: string }[] {
    const rows = [];
    const visit = (node: ChartNode, parent: ChartNode | null, level: number, parentPath: string) => {
      const path = parentPath ? `${parentPath} / ${node.name}` : node.name;
      rows.push({ node, parent, level, path });
      (includeEmployees ? node.employees : []).forEach(emp => rows.push({ node: emp, parent: node, level: level + 1, path: `${path} / ${emp.name}` }));
      node.children.forEach(child => visit(child, node, level + 1, path));
    };
    roots.forEach(root => visit(root, null, 1, ''));
    return rows;
  }

  // Paged exports

  // Area the chart is fitted into on a PDF page.
  pdfChartBox(paper: PaperSize): Size {
    const page = PAPER_SIZES[paper];
    return {
      width: page.width - 2 * PAGE_MARGIN,
      height: page.height - 2 * PAGE_MARGIN - PAGE_HEADER - PAGE_FOOTER,
    };
  }

  // Area the chart image is fitted into on a slide, in points so it compares with the PDF box.
  slideChartBox(): Size {
    return { width: SLIDE_IMAGE.width * POINTS_PER_INCH, height: SLIDE_IMAGE.height * POINTS_PER_INCH };
  }

  async exportPagedPdf(title: string, paper: PaperSize, pages: ExportPage[], onProgress?: (page: number) => void) {
    saveAs(await this.buildPagedPdf(title, paper, pages, onProgress), `${title}.pdf`);
  }

  // Returns the PDF as a data URL.
  async buildPagedPdf(title: string, paper: PaperSize, pages: ExportPage[], onProgress?: (page: number) => void): Promise<string> {
    const box = this.pdfChartBox(paper);
    const document = new Group();

    for (let index = 0; index < pages.length; index++) {
      onProgress?.(index + 1);
      const element = await pages[index].render();
      const content = await this.draw(element);

      const chart = this.placeInBox(content, box, [0, PAGE_HEADER]);

      const page = new Group();
      page.append(
        new Text(title, [0, 0], { font: 'bold 16px sans-serif', fill: { color: '#424242' } }),
        new Text(pages[index].subtitle, [0, 22], { font: '11px sans-serif', fill: { color: '#757575' } }),
        chart,
        new Text(`Page ${index + 1} of ${pages.length}`, [0, PAGE_HEADER + box.height + 6], { font: '9px sans-serif', fill: { color: '#9e9e9e' } }),
      );
      document.append(page);
    }

    const size = PAPER_SIZES[paper];
    return exportPDF(document, {
      title,
      multiPage: true,
      paperSize: [size.width, size.height],
      margin: { top: PAGE_MARGIN, right: PAGE_MARGIN, bottom: PAGE_MARGIN, left: PAGE_MARGIN },
    });
  }

  // Scales `content` down to fit `box` (never up) and centres it horizontally at `offset`.
  // Uses one transform per group: Kendo's fit()/align() mis-scale text in PDF output.
  private placeInBox(content: Group, box: Size, offset: [number, number]): Group {
    const bbox = content.bbox();
    const width = bbox.size.width;
    const height = bbox.size.height;
    const scale = Math.min(1, box.width / width, box.height / height);

    const origin = new Group({ transform: geometry.transform().translate(-bbox.origin.x, -bbox.origin.y) });
    origin.append(content);
    const scaled = new Group({ transform: geometry.transform().scale(scale, scale) });
    scaled.append(origin);
    const placed = new Group({ transform: geometry.transform().translate(offset[0] + (box.width - width * scale) / 2, offset[1]) });
    placed.append(scaled);
    return placed;
  }

  async exportPowerPoint(title: string, pages: ExportPage[], onProgress?: (page: number) => void) {
    const pptx = new pptxgen();
    pptx.layout = 'LAYOUT_WIDE';
    pptx.title = title;

    for (let index = 0; index < pages.length; index++) {
      onProgress?.(index + 1);
      const image = await this.captureImage(await pages[index].render());

      const slide = pptx.addSlide();
      slide.addText(title, { x: 0.4, y: 0.2, w: SLIDE_IMAGE.width, h: 0.45, fontSize: 20, bold: true, color: '424242' });
      slide.addText(pages[index].subtitle, { x: 0.4, y: 0.6, w: SLIDE_IMAGE.width, h: 0.3, fontSize: 12, color: '757575' });
      slide.addText(`Slide ${index + 1} of ${pages.length}`, { x: 0.4, y: SLIDE.height - 0.45, w: 3, h: 0.3, fontSize: 9, color: '9e9e9e' });

      // Fit inside the image area without enlarging past the size the planner allowed for
      const ratio = Math.min(SLIDE_IMAGE.width / image.width, SLIDE_IMAGE.height / image.height, 1 / POINTS_PER_INCH);
      const w = image.width * ratio;
      const h = image.height * ratio;
      slide.addImage({ data: image.dataUrl, x: SLIDE_IMAGE.x + (SLIDE_IMAGE.width - w) / 2, y: SLIDE_IMAGE.y, w, h });
    }

    await pptx.writeFile({ fileName: `${title}.pptx` });
  }
}
